import React, { ReactElement } from 'react';
import Timeline from 'sections/Benefits/PaymentProtectionPage/Timeline';
import RootLayout from 'layouts/RootLayout';
import Breadcrumbs from 'components/Breadcrumbs';
import Link from 'next/link';

interface IPlaqueData {
  id: number;
  icon: string;
  title: string;
  text: string;
}

interface ISchemeData {
  id: number;
  icon: string;
  strongWord: string;
  text: string;
  type: string;
}

const plaqueData: IPlaqueData[] = [
  {
    id: 0,
    icon: '1.svg',
    title: 'DFA payment protection',
    text: 'covers only ocean freight invoices related to the offices/cities that are registered/listed in the Directory. We encourage you to list all your offices and branches with DFA to get the maximum coverage. ',
  },
  {
    id: 1,
    icon: '2.svg',
    title: 'Premium insurance',
    text: 'Premium DFA members are automatically covered by a best in class credit risk insurance. The insurance starts at US$10.000 per year, and can accumulate to a maximum of US$100.000 for same period.',
  },
];

const schemeData: ISchemeData[] = [
  {
    id: 0,
    icon: '2.svg',
    strongWord: 'Premium ',
    text: ` DFA members are automatically covered by a best in class <a class='new-link' href='/benefits/payment-protection'>credit risk insurance</a>. The insurance starts at US$ 10.000 up to US $100.000 per year. Example: you haven’t received a payment in amount of $100.000 from another DFA premium member for <a class='new-link' href='#anchor__premium'>earlier acknowledged and approved by DFA team deal</a>. In the case you are a premium member, DFA will cover the amount from US$10.000 up to US$100.000 per year as per passed PP verification <span class="tooltip__box"><img src="/images/main/payment-protection2/scheme/5.svg" alt="tooltip" class="tooltip__icon" data-type='Please follow Member’s Kit, page 8, point 2.2.1 KYC process'/></span>.`,
    type: 'Submitted long KYC form and provided all required documents.',
  },
  {
    id: 1,
    icon: '3.svg',
    strongWord: 'Standard Active ',
    text: ' DFA members - PPP is temporarily in development.',
    type: 'Rates listed, bookings closed with Searates.',
  },
  {
    id: 2,
    icon: '4.svg',
    strongWord: 'Standard Inactive ',
    text: ' DFA members are not covered by the network credit insurance.',
    type: 'Haven’t submitted KYC (without required documents).',
  },
];

const checklist: Array<string[]> = [
  [
    'Current account (Signed and with corporate seal).',
    'Invoices (Signed and with corporate seal).',
    'Sales agreement (If any; signed and with corporate seal).',
    'Purchases order / LPO (Signed and with corporate seal).',
    'Delivery forms (Signed and with corporate seal).',
    'Trading history of past 12 months with the counterparty reflecting at least 2 positive transactions on credit.',
  ],
  [
    'Please ensure there are NO overdues beyond the 60 days from payment due date for.',
    'Copies of bounced cheques, bank reports and police reports.',
    'All documents concerning the debt (if any) (contract, commercial bill).',
    'Copies of any correspondence (Emails, fax etc) between buyer and creditor about the debt.',
    'Written notice (signed and with corporate seal) indicating that related invoices have been fully declared to us by the “turnover declaration” for the specific period.',
  ],
];

const coveredListData = [['Payment default arising form a trade transaction between DFA Premium Members.']];

const notCoveredListData = [
  [
    'Intercompany transactions despite being members would not be covered.',
    'Subsidiaries of members that are not part of the DFA PCM (Each local office of a group must also be a paid member).',
    'Claims relating to cargo value/loss of earnings from freight (demurrage costs).',
    'Payment for the cost of the cargo, lost or damaged, or cargo.',
    'Containers, or lost or damaged cargo containers – Issues arising with actual cargo which fall under cargo insurance.',
    'Losses caused by intervening acts of third parties, such as fraud, false documentation, or other criminal or tortious acts.',
    'THE MEMBER SENDING THE PAYMENT IS RESPONSIBLE TO ENSURE THAT HE IS SENDING IT TO THE CORRECT BANKING ACCOUNT AND BANKING FACILITY.',
    'Cybercrime is NOT covered.',
  ],
];

const management: string[] = [
  'Ensure that the Company Name on DFA’s portal is as per the Trade License',
  'Keep a tab on the Trade License Expiry Date of your counterpart',
  'Payment terms/Due date always need to be mentioned on the invoice',
  'An invoice without payment terms is deemed to be a cash transaction',
  'Invoice value needs to be mentioned with the right currency',
  'There always needs to be an acknowledgment of delivery of goods/services',
];

export default function PaymentProtectionPage() {
  const mapList = (item: string[]): React.ReactElement => {
    return (
      <>
        {item.map((element, index) => {
          return (
            <li key={`${element.length}_${index}`} className="payment-protection__list-item">
              {element}
            </li>
          );
        })}
      </>
    );
  };

  const tooltipOn = (event: React.MouseEvent) => {
    const element: any = event.target;

    event.stopPropagation();

    if (!element.dataset.type) {
      return;
    }

    const tooltipElBox = document.createElement('div');
    tooltipElBox.classList.add('tooltip__container-box');

    const tooltipEl = document.createElement('object');
    tooltipEl.classList.add('tooltip__image');
    tooltipEl.data = '/images/main/payment-protection2/vector.svg';
    tooltipEl.type = 'image/svg+xml';

    const tooltipText = document.createElement('span');
    tooltipText.classList.add('tooltip__text');

    tooltipText.innerHTML = element.dataset.type;
    tooltipElBox.append(tooltipText);
    tooltipElBox.append(tooltipEl);

    element.parentElement.append(tooltipElBox);

    setTimeout(() => {
      tooltipElBox.style.opacity = '1';
    }, 700);
  };

  const tooltipOff = () => {
    const tooltipList = document.querySelectorAll('.tooltip__container-box');

    tooltipList.forEach((item: any) => {
      item.style.opacity = '0';

      setTimeout(() => {
        item.remove();
      }, 200);
    });
  };

  return (
    <main className="payment-protection">
      <section className="payment-protection__intro">
        <div className="container">
          <Breadcrumbs
            mode="light"
            links={[
              { title: 'Benefits', href: '/benefits' },
              { title: 'Payment protection', href: '' },
            ]}
          />
          <div className="payment-protection__intro-header">
            <div className="payment-protection__intro-header-description-box">
              <h1 className="payment-protection__intro-header-title">Payment Protection Benefits</h1>
              <div className="payment-protection__intro-header-text-box">
                <p className="payment-protection__intro-header-text">
                  The main objective of Digital Freight Alliance is to build a stable and long-term partnership between
                  all members of the network, in which all premium participants enjoy the luxury of financial confidence
                  when conducting business with other{' '}
                  <Link href="/membership" className="link">
                    premium members
                  </Link>{' '}
                  in the group under the most complete guarantees.
                </p>
                <p className="payment-protection__intro-header-text">
                  DFA transactions are guaranteed between enrolled member offices in the case of non-payment. In this
                  respect, we are happy to announce the launch of the DFA Protection Plan, as part of the benefits of
                  being a premium member of the network.
                </p>
              </div>
              <a className="payment-protection__intro-header-button" href={'/company/contact-us'}>
                Contact us
              </a>
            </div>
            <div className="payment-protection__intro-header-image-box">
              <img
                src="/images/main/payment-protection2/2.png"
                alt="Payment protection"
                className="payment-protection__intro-header-image"
              />
            </div>
          </div>
          <div className="payment-protection__intro-plaque">
            {plaqueData.map(({ icon, title, id, text }) => {
              return (
                <div className="payment-protection__intro-plaque-item" key={id}>
                  <img
                    src={`/images/main/payment-protection2/plaque/${icon}`}
                    alt={title}
                    className="payment-protection__intro-plaque-image"
                  />
                  <h3 className="payment-protection__intro-plaque-title">{title}</h3>
                  <p className="payment-protection__intro-plaque-text">{text}</p>
                </div>
              );
            })}
          </div>
          <div className="payment-protection__intro-scheme">
            <h2 className="payment-protection__intro-scheme-title">
              Payment
              <span className="word-part">
                Protection
                <img src="/images/main/payment-protection2/scheme/1.svg" alt="Design line" className="vector" />
              </span>
              Scheme
            </h2>
            <div
              className="payment-protection__intro-scheme-list"
              onMouseOut={() => tooltipOff()}
              onMouseOver={(event) => tooltipOn(event)}
            >
              {schemeData.map(({ id, icon, strongWord, text, type }) => {
                return (
                  <div className="payment-protection__intro-scheme-item" key={id}>
                    <img
                      className="payment-protection__intro-scheme-item-image"
                      src={`/images/main/payment-protection2/scheme/${icon}`}
                      alt={strongWord}
                    />
                    <p className="payment-protection__intro-scheme-item-text">
                      <span className="word-part">{strongWord}</span>
                      <span className="tooltip__box">
                        <img
                          src="/images/main/payment-protection2/scheme/5.svg"
                          alt="tooltip"
                          className="tooltip__icon"
                          data-type={type}
                        />
                      </span>
                      <span dangerouslySetInnerHTML={{ __html: text }} />
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="payment-protection__coverage">
        <div className="container">
          <div className="payment-protection__coverage-box">
            <div className="payment-protection__coverage-description-box">
              <h2 className="payment-protection__coverage-heading">What is covered</h2>
              {coveredListData.map((item, index) => {
                return (
                  <ul className="payment-protection__list" key={`${item.length}_${index}`}>
                    {mapList(item)}
                  </ul>
                );
              })}
            </div>
            <div className="payment-protection__coverage-image-box">
              <img
                className="payment-protection__coverage-image"
                src="/images/pages/Benefits/PaymentProtection/coverage-bg.png"
                alt="image"
              />
            </div>
          </div>
          <div className="payment-protection__coverage-box">
            <h2 className="payment-protection__coverage-heading">What will not be covered</h2>
            {notCoveredListData.map((item, index) => {
              return (
                <ul className="payment-protection__list" key={`${item.length}_${index}`}>
                  {mapList(item)}
                </ul>
              );
            })}
          </div>
        </div>
      </section>

      <section className="payment-protection__checklist">
        <div className="container">
          <div className="payment-protection__checklist-description">
            <h2 className="payment-protection__checklist-title">Claims Document</h2>
            <h2 className="payment-protection__checklist-subtitle">Checklist</h2>
            <p className="payment-protection__checklist-text">
              Note – The above points are important but NOT the only few & there could be more information/supporting
              documents requested during the claims assessment. – DFA Payment protection is extended only for the paid
              members who have provided all the required KYC, onboarding documents & have been successfully confirmed to
              be covered by the DFA team after necessary credit risk assessment processes & subsequent approvals.
            </p>
          </div>
          <div className="payment-protection__checklist-box">
            {checklist.map((item, index) => {
              return (
                <ul className="payment-protection__list" key={`${item.length}_${index}`}>
                  {mapList(item)}
                </ul>
              );
            })}
          </div>
        </div>
      </section>

      <section className="payment-protection__management">
        <div className="container">
          <div className="payment-protection__management-content">
            <div className="payment-protection__management-image-box">
              <img
                className="payment-protection__management-gradient gradient-1"
                src="/images/main/payment-protection2/management/1.png"
                alt="Cargo management"
              />
              <img
                className="payment-protection__management-image"
                src="/images/main/payment-protection2/3.png"
                alt="Master Card"
              />
              <img
                className="payment-protection__management-gradient gradient-2"
                src="/images/main/payment-protection2/management/1.png"
                alt="Gradient background"
              />
            </div>
            <div className="payment-protection__management-list-box">
              <h2 className="payment-protection__management-title">Credit Management</h2>
              <p className="payment-protection__management-subtitle">
                Key Points for members to follow as a good practice
              </p>
              <ul className="payment-protection__management-list">
                {management.map((item, index) => {
                  return (
                    <li className="payment-protection__management-item" key={`${item.length}_${index}`}>
                      {item}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="payment-protection__timeline">
        <div className="container">
          <div className="payment-protection__timeline-header-box">
            <h2 className="payment-protection__timeline-title">Claims timeline process for DFA</h2>
            <p className="payment-protection__timeline-subtitle">
              Obtain settlement within 90 days after filing all necessary paperwork. This is the market's fastest
              option.
            </p>
            <img
              className="payment-protection__timeline-gradient"
              src="/images/main/payment-protection2/timeline/1.png"
              alt="Digital Freight Timeline"
            />
          </div>
        </div>

        <Timeline />

        <div className="container">
          <div className="payment-protection__disclaimer-box">
            <span className="payment-protection__disclaimer">
              <mark className="payment-protection__marker">The DFA PPP</mark> only applies to members in the event of
              non-payments resulting from a shipping transaction between verified premium members.
            </span>
            <br />
            <br />
            <span id="anchor__premium" className="payment-protection__disclaimer">
              Before the start, every transaction between DFA members should be reported to DFA Support Team as per the{' '}
              <a rel="nofollow" href="https://telegra.ph/DFA-Transaction-Template-Form-10-20" target="_blank">
                standardized form
              </a>{' '}
              sent in email by the executing party, and DFA Support Team should confirm in writing the transaction falls
              under PPP. Please note PPP will not cover any transactions without written DFA Support Team confirmation (
              <a href="mailto:support@df-alliance.com" target="_blank">
                support@df-alliance.com
              </a>
              )
            </span>
            <br />
            <br />
            <span className="payment-protection__disclaimer">
              PPP does not cover demurrage and detention charges, or other transportation charges outside ocean freight
              . PPP also does not cover charges which may arise as a result of a commercial dispute between the buyer
              and seller of goods.
            </span>
          </div>
        </div>
      </section>
    </main>
  );
}

export const getStaticProps = async () => {
  return {
    props: {
      title:
        'Payment Protection Benefits | DF Alliance',
      description:
        'Benefit from DF Alliance\'s payment protection services, ensuring secure and reliable financial transactions during your global shipping operations.',
      path: '/benefits/payment-protection',
    },
  };
};

PaymentProtectionPage.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};

// Todo: new version of the page. Should be merged with the current version after approval.

// import React, { ReactElement } from 'react';
// import RootLayout from 'layouts/RootLayout';
// import Link from 'next/link';
// import Breadcrumbs from 'components/Breadcrumbs';
// import { PitchTitleIcon, IconCross } from 'sections/Benefits/svg';
// import { cards, faq } from 'sections/Benefits/PaymentProtection/data';
// import useWindowSize from 'utils/hooks/useWindowSize';
// import Form from 'sections/Benefits/components/Form';
// import { LeadFormParameters } from 'sections/Benefits/data';

// type PaymentProtectionProps = {
//   pageTitle?: string;
//   formUrl?: string;
// };

// export default function PaymentProtectionPage({ pageTitle, formUrl }: PaymentProtectionProps) {
//   const { width: screenWidth } = useWindowSize(); 

//   const cardsList = cards.map((data) => (
//     <li className="payment-protection__cards-item" key={data.icon}>
//       <div className="payment-protection__cards-item-title-box">
//         <div className="payment-protection__cards-item-icon">
//           <img src={`/images/pages/Benefits/${data.icon}`} alt={data.alt} />
//         </div>
//         <h3 className="payment-protection__cards-item-title">{data.title}</h3>
//       </div>
//       <p className="payment-protection__cards-item-description">{data.description}</p>
//       <div className="payment-protection__cards-item-links-box">
//       </div>
//     </li>
//   ));
  
//   const faqList = faq.map((item) => {
//     return (
//       <div className="payment-protection__faq-list-item" key={item.id}>
//         <input type="checkbox" name={item.id} id={item.id}/>
//         <label htmlFor={item.id} className="payment-protection__faq-list-item-label">
//             <p>{item.question}</p>
//             <div>{IconCross}</div>
//           </label>
//         <div className="payment-protection__faq-list-item-content">
//           <p className="payment-protection__faq-list-item-content-text">{item.answer}</p>
//         </div>
//       </div>
//     );
//   });

//   return (
//     <div className="payment-protection">
//       {/* SECTION: HEADER HERO */}
//       <section className="payment-protection__header-hero">
//         <div className="container">
//           <Breadcrumbs
//             mTop={64}
//             mode="light"
//             links={[
//               { title: 'Benefits', href: '/benefits' },
//               { title: 'Payment Protection', href: '' },
//             ]}
//           />
//           <div className="payment-protection__header-hero-content">
//             <div className="payment-protection__header-hero-title-box">
//               <h1 className="payment-protection__header-hero-title">
//                 <span>Protect Your Freight Payments</span> 
//                 <span>with DFA</span>
//               </h1>
//               {screenWidth >= 769 
//                 ?
//                 <p className="payment-protection__header-hero-title-description">
//                   <span>Join Digital Freight Alliance and secure your transactions with industry-leading payment</span>
//                   <span>protection, backed by robust credit insurance and quick claim resolutions</span>
//                 </p>
//                 :
//                 <p className="payment-protection__header-hero-title-description">
//                   Join Digital Freight Alliance and secure your transactions with industry-leading payment protection, 
//                   backed by robust credit insurance and quick claim resolutions.
//                 </p>
//               }
//             </div>
//             <div className="payment-protection__header-hero-buttons-box">
//               <Link className="new-link-block full" href="/membership">
//                 Get Started Today
//               </Link>
//             </div>
//           </div>
//         </div>
//       </section>

//       {/* SECTION: PITCH */}
//       <section className="payment-protection__pitch">
//         <div className="container">
//           <div className="payment-protection__pitch-title-box">
//             <h2 className="payment-protection__pitch-title">
//               {PitchTitleIcon}
//               Secure Transactions, Reliable Business
//             </h2>
//             <p className="payment-protection__pitch-title-description">
//               The foundation of trust in global freight forwarding
//             </p>
//           </div>
//           <div className="payment-protection__pitch-paragraphs">
//             <p className="payment-protection__pitch-paragraph">
//               At Digital Freight Alliance, payment protection isn’t just a feature – it’s our promise to you. By leveraging DFA's robust credit risk insurance, premium members enjoy unrivalled financial security on verified transactions with other premium members. Our Payment Protection Plan safeguards against non-payments, ensuring your business stays financially resilient and uninterrupted. From handling claims swiftly within 90 days to offering coverage that scales up to $100,000, we’re here to ensure your transactions are as seamless as your logistics. Join DFA today and conduct business with confidence, knowing your payments are always protected.
//             </p>
//           </div>
//           <div className="payment-protection__pitch-buttons">
//             <Link className="new-link-block full" href="/membership">
//               Join DFA Alliance Now
//             </Link>
//           </div>
//         </div>
//       </section>

//       {/* SECTION: CARDS */}
//       <section className="payment-protection__cards">
//         <div className="container">
//           <div className="payment-protection__cards-title-box">
//             {screenWidth >= 576
//               ?
//               <h2 className="payment-protection__cards-title">
//                 <span>Maximise Your Business Potential</span> 
//                 <span>with DFA Networking</span>
//               </h2>
//               :
//               <h2 className="payment-protection__cards-title">
//                 Maximise Your Business Potential with DFA Networking
//               </h2>
//             }
//             {screenWidth >= 576
//               ?
//               <p className="payment-protection__cards-title-description">
//                 <span>Discover how our networking benefits can elevate your</span> 
//                 <span>freight forwarding operations.</span>
//               </p>
//               :
//               <p className="payment-protection__cards-title-description">
//                 Discover how our networking benefits can elevate your freight forwarding operations. 
//               </p>
//             }
//           </div>
//             <ul className="payment-protection__cards-list">
//               {cardsList}
//           </ul>
//         </div>
//       </section>

//       {/* SECTION: FQA */}
//       <section className="payment-protection__faq">
//         <div className="container">
//           <h2 className="payment-protection__faq-title">Frequently Asked Questions</h2>
//           <ul className="payment-protection__faq-list">
//             {faqList}
//           </ul>
//         </div>
//       </section>

//       {/* SECTION FORM */}
//       <section className="payment-protection__form">
//         <div className="container">
//           <div className="payment-protection__form-title-box">
//             <h2 className="payment-protection__form-title">
//               Get Protected. Grow Confidently
//             </h2>
//             <p className="payment-protection__form-title-description">
//               Secure your payments and build lasting partnerships with DFA’s Payment Protection Plan.
//             </p>    
//           </div>
//           <div className="payment-protection__form-content-box">
//             <ul className="payment-protection__form-content-list">
//               <li className="payment-protection__form-content-list-item">
//                 Enjoy financial security with coverage up to $100,000 per year.
//               </li>
//               <li className="payment-protection__form-content-list-item">
//                 Avoid cash flow interruptions with claims processed in just 90 days.
//               </li>
//               <li className="payment-protection__form-content-list-item">
//                 Foster trust and reliability in your global trade partnerships.
//               </li>
//               <li className="payment-protection__form-content-list-item">
//                 Simplify transactions with guaranteed support from our team.
//               </li>
//               <li className="payment-protection__form-content-list-item">
//                 Scale your business with peace of mind in every deal.
//               </li>
//             </ul>
//             <Form 
//               pageTitle={pageTitle ?? ''} 
//               formUrl={formUrl ?? ''} 
//               buttonText='Join Us Now' 
//               heading='Sign Up for Payment Protection Now'
//             />
//           </div>
//         </div>
//       </section>
//     </div>
//   );
// }

// export const getStaticProps = async () => {
//   // 'FormParameters' - parameters, passed to 'Form' component on all Benefits pages as an additional data: 
//   const FormParameters = {
//     pageTitle: LeadFormParameters.paymentProtection.pageTitle,
//     formUrl: LeadFormParameters.paymentProtection.formUrl,
//   };

//   return {
//     props: {
//       title:
//         'DFA Payment protection & Insurance Plans to Safeguard your work processes with the partner safely and securely',
//       description:
//         'DFA Offers Payment Protection & Liability Insurance to work safely & securely with your partners. Online Business Protection, Leading Insurance Companies, & Guarantee of Payment',
//       path: '/benefits/payment-protection',
//       ...FormParameters,
//     },
//   };
// };

// PaymentProtectionPage.getLayout = function getLayout(page: ReactElement) {
//   return <RootLayout>{page}</RootLayout>;
// };
